import { useState, useEffect, useMemo } from 'react'
import { useFStore } from '@/stores/useFStore'
import { fetchCard } from '@/lib/api'
import type { CardDetail } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ArrowLeft, Check, Eye, Sparkles } from 'lucide-react'

interface CardTableProps {
  cards: {
    card_id: string
    title: string
    domain: string
    similarity?: number
    isIntegrated?: boolean
  }[]
  onCardClick: (cardId: string) => void
}

function CardTable({ cards, onCardClick }: CardTableProps) {
  if (cards.length === 0) {
    return (
      <div className="text-center py-8 px-4">
        <p className="text-sm text-muted-foreground">No cards to display</p>
      </div>
    )
  }

  return (
    <table className="w-full text-xs">
      <thead className="bg-background sticky top-0 border-b border-border">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Title</th>
          <th className="px-3 py-2 text-left font-medium w-20">Space</th>
          <th className="px-3 py-2 text-right font-medium w-16">Match</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {cards.map((card) => (
          <tr
            key={card.card_id}
            onClick={() => onCardClick(card.card_id)}
            className={cn(
              'cursor-pointer transition-colors',
              card.isIntegrated
                ? 'bg-green-500/10 hover:bg-green-500/20'
                : 'hover:bg-muted/30'
            )}
          >
            <td className="px-3 py-2">
              <div className="flex items-center gap-1.5">
                {card.isIntegrated && (
                  <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                )}
                <span
                  className={cn(
                    'font-medium line-clamp-1',
                    card.isIntegrated
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-foreground'
                  )}
                >
                  {card.title}
                </span>
              </div>
            </td>
            <td
              className={cn(
                'px-3 py-2 capitalize',
                card.isIntegrated
                  ? 'text-green-600/70 dark:text-green-400/70'
                  : 'text-muted-foreground'
              )}
            >
              {card.domain?.toLowerCase() || '—'}
            </td>
            <td className="px-3 py-2 text-right text-muted-foreground">
              {card.similarity ? `${Math.round(card.similarity * 100)}%` : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

interface CardDetailViewProps {
  cardId: string
  onBack: () => void
}

function CardDetailView({ cardId, onBack }: CardDetailViewProps) {
  const [card, setCard] = useState<CardDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const { integratedCardIds } = useFStore()
  const isIntegrated = integratedCardIds.includes(cardId)

  useEffect(() => {
    setLoading(true)
    fetchCard(cardId)
      .then(setCard)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [cardId])

  if (loading) {
    return (
      <div className="p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </div>
    )
  }

  if (!card) {
    return (
      <div className="p-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <p className="text-sm text-muted-foreground">Card not found</p>
      </div>
    )
  }

  return (
    <div className="p-4 overflow-y-auto">
      <button
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back
      </button>

      <div className="space-y-4">
        {/* Header */}
        <div>
          <div className="flex items-start gap-2">
            {isIntegrated && (
              <span className="px-2 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full text-xs flex items-center gap-1">
                <Check className="w-3 h-3" />
                Integrated
              </span>
            )}
          </div>
          <h3 className="text-lg font-medium text-foreground mt-2">
            {card.title || 'Untitled Card'}
          </h3>
        </div>

        {/* Metadata */}
        <div className="flex flex-wrap gap-2 text-xs">
          {card.domain && (
            <span className="px-2 py-1 bg-muted rounded capitalize">
              {card.domain}
            </span>
          )}
          {card.type && (
            <span className="px-2 py-1 bg-muted rounded capitalize">
              {card.type}
            </span>
          )}
          {card.quality_score && (
            <span className="px-2 py-1 bg-muted rounded">
              Quality: {Math.round(card.quality_score * 100)}%
            </span>
          )}
        </div>

        {/* Summary */}
        {card.summary && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1">
              Summary
            </h4>
            <p className="text-sm text-foreground">{card.summary}</p>
          </div>
        )}


        {/* Tags */}
        {card.tags && card.tags.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-muted-foreground mb-1">
              Tags
            </h4>
            <div className="flex flex-wrap gap-1">
              {card.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-muted rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

type TabType = 'surfaced' | 'integrated'

export function FCardsPanel() {
  const { surfacedCards, integratedCardIds, workflowStatus, selectedCardId, setSelectedCardId } = useFStore()
  const [activeTab, setActiveTab] = useState<TabType>('surfaced')
  const [fetchedIntegratedCards, setFetchedIntegratedCards] = useState<
    Map<string, { title: string; domain: string; failed?: boolean }>
  >(new Map())

  const isStreaming = workflowStatus === 'streaming'

  // Find integrated card IDs that aren't in surfacedCards
  const missingIntegratedIds = useMemo(() => {
    const surfacedIds = new Set(surfacedCards.map((c) => c.card_id))
    return integratedCardIds.filter((id) => !surfacedIds.has(id))
  }, [surfacedCards, integratedCardIds])

  // Fetch missing integrated cards
  useEffect(() => {
    if (missingIntegratedIds.length === 0) return

    // Only fetch cards we don't already have (including failed ones)
    const idsToFetch = missingIntegratedIds.filter(
      (id) => !fetchedIntegratedCards.has(id)
    )
    if (idsToFetch.length === 0) return

    // Fetch cards in parallel
    Promise.all(
      idsToFetch.map((id) =>
        fetchCard(id)
          .then((card) => ({ id, card, failed: false }))
          .catch(() => ({ id, card: null, failed: true }))
      )
    )
      .then((results) => {
        setFetchedIntegratedCards((prev) => {
          const next = new Map(prev)
          for (const { id, card, failed } of results) {
            if (card) {
              next.set(card.card_id, {
                title: card.title || 'Untitled',
                domain: card.domain || '',
              })
            } else if (failed) {
              // Mark as failed so we don't retry
              next.set(id, {
                title: `Card ${id.slice(0, 8)}...`,
                domain: '',
                failed: true,
              })
            }
          }
          return next
        })
      })
      .catch(console.error)
  }, [missingIntegratedIds, fetchedIntegratedCards])

  // For surfaced tab, show all surfaced cards with integration status
  const surfacedCardsWithStatus = surfacedCards.map((c) => ({
    card_id: c.card_id,
    title: c.title || 'Untitled',
    domain: c.domain || '',
    similarity: c.similarity,
    isIntegrated: integratedCardIds.includes(c.card_id),
  }))

  // For integrated tab - combine surfaced cards + fetched cards
  const integratedCardsDisplay = useMemo(() => {
    const result: {
      card_id: string
      title: string
      domain: string
      similarity?: number
      isIntegrated: boolean
    }[] = []

    for (const cardId of integratedCardIds) {
      // Check surfaced cards first
      const surfaced = surfacedCards.find((c) => c.card_id === cardId)
      if (surfaced) {
        result.push({
          card_id: surfaced.card_id,
          title: surfaced.title || 'Untitled',
          domain: surfaced.domain || '',
          similarity: surfaced.similarity,
          isIntegrated: true,
        })
      } else {
        // Check fetched cards
        const fetched = fetchedIntegratedCards.get(cardId)
        if (fetched) {
          result.push({
            card_id: cardId,
            title: fetched.title,
            domain: fetched.domain,
            isIntegrated: true,
          })
        } else {
          // Card not yet fetched - show placeholder with truncated ID
          result.push({
            card_id: cardId,
            title: cardId.length > 20 ? `Card ${cardId.slice(0, 8)}...` : `Card ${cardId}`,
            domain: '',
            isIntegrated: true,
          })
        }
      }
    }

    return result
  }, [integratedCardIds, surfacedCards, fetchedIntegratedCards])

  if (selectedCardId) {
    return (
      <CardDetailView
        cardId={selectedCardId}
        onBack={() => setSelectedCardId(null)}
      />
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Tab bar */}
      <div className="flex-shrink-0 flex items-center gap-1 px-3 py-2 border-b border-border">
        <button
          onClick={() => setActiveTab('surfaced')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
            activeTab === 'surfaced'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Surfaced</span>
          {surfacedCards.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-muted rounded-full text-xs">
              {surfacedCards.length}
            </span>
          )}
          {isStreaming && (
            <span className="ml-1 w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
          )}
        </button>

        <button
          onClick={() => setActiveTab('integrated')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded text-sm transition-colors',
            activeTab === 'integrated'
              ? 'bg-muted/50 text-foreground'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
          )}
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="font-light">Integrated</span>
          {integratedCardIds.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-green-500/20 text-green-600 dark:text-green-400 rounded-full text-xs">
              {integratedCardIds.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'surfaced' ? (
          surfacedCards.length === 0 ? (
            <div className="h-full flex items-center justify-center px-6 text-center">
              <div className="text-muted-foreground text-sm max-w-sm space-y-2">
                <p className="font-medium text-foreground">Surfaced Cards</p>
                <p>
                  As you chat, relevant cards from your library will appear
                  here.
                </p>
                <p className="text-xs">
                  These are cards that match the context of your conversation.
                </p>
              </div>
            </div>
          ) : (
            <CardTable
              cards={surfacedCardsWithStatus}
              onCardClick={setSelectedCardId}
            />
          )
        ) : integratedCardIds.length === 0 ? (
          <div className="h-full flex items-center justify-center px-6 text-center">
            <div className="text-muted-foreground text-sm max-w-sm space-y-2">
              <p className="font-medium text-foreground">Integrated Cards</p>
              <p>
                Cards that the AI weaves into your Foolscap document will appear
                here.
              </p>
              <p className="text-xs">
                The AI naturally integrates card insights to enrich your story
                structure.
              </p>
            </div>
          </div>
        ) : (
          <CardTable
            cards={integratedCardsDisplay}
            onCardClick={setSelectedCardId}
          />
        )}
      </div>
    </div>
  )
}
